import React, { useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { Menu, X, ArrowRight } from 'lucide-react'

const NAV = [
  { to: '/features', label: 'Features' },
  { to: '/pricing',  label: 'Pricing'  },
  { to: '/about',    label: 'About'    },
  { to: '/faq',      label: 'FAQ'      },
  { to: '/contact',  label: 'Contact'  },
]

export default function MarketingLayout() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  // Close mobile menu on navigation.
  React.useEffect(() => { setOpen(false) }, [pathname])

  return (
    <div className="min-h-screen bg-cream-50 font-body flex flex-col">
      <MarketingNav open={open} setOpen={setOpen} />
      <main className="flex-1">
        <Outlet />
      </main>
      <MarketingFooter />
    </div>
  )
}

function MarketingNav({ open, setOpen }) {
  return (
    <nav className="sticky top-0 z-50 bg-cream-50/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <img src="/images/logo.png" alt="Tabowe" className="w-11 h-11" />
          <span className="font-display font-700 text-xl text-forest-900">tabowe</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'text-forest-900 bg-forest-100/60'
                    : 'text-forest-800/80 hover:text-forest-900 hover:bg-forest-100/40'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2">
          <Link to="/login" className="btn-ghost text-forest-800 text-sm">Sign in</Link>
          <Link to="/register" className="btn-primary !py-2 !px-4 !text-sm gap-1.5">
            Get started <ArrowRight size={14} />
          </Link>
        </div>

        <button
          type="button"
          className="md:hidden p-2 text-forest-900"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen(v => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-gray-100 bg-cream-50">
          <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm font-medium rounded-lg ${
                    isActive ? 'bg-forest-100 text-forest-900' : 'text-forest-800 hover:bg-forest-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex gap-2 mt-3">
              <Link to="/login" className="btn-secondary flex-1 justify-center text-sm">Sign in</Link>
              <Link to="/register" className="btn-primary flex-1 justify-center text-sm">Get started</Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

function MarketingFooter() {
  return (
    <footer className="bg-forest-950 text-forest-300">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-md bg-gold-500 flex items-center justify-center">
                <span className="text-forest-900 font-display font-800 text-xs">T</span>
              </div>
              <span className="font-display font-700 text-white">tabowe</span>
            </div>
            <p className="text-sm text-forest-400 max-w-xs">
              The smarter, more generous way to split expenses with anyone.
            </p>
          </div>

          <FooterCol title="Product" links={[
            { to: '/features', label: 'Features' },
            { to: '/pricing',  label: 'Pricing' },
            { to: '/faq',      label: 'FAQ' },
          ]} />

          <FooterCol title="Company" links={[
            { to: '/about',    label: 'About' },
            { to: '/contact',  label: 'Contact' },
          ]} />

          <FooterCol title="Legal" links={[
            { to: '/privacy',  label: 'Privacy' },
            { to: '/terms',    label: 'Terms' },
          ]} />
        </div>

        <div className="pt-8 border-t border-forest-800 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-forest-400">
          <p>© {new Date().getFullYear()} Tabowe. All rights reserved.</p>
          <p>Made with care · tabowe.com</p>
        </div>
      </div>
    </footer>
  )
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-white font-display font-600 text-sm mb-3">{title}</h4>
      <ul className="space-y-2">
        {links.map(l => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-forest-300 hover:text-white transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
